import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { AccountStatus, IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import { useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { useMutationCreateAccount } from "../../../../query/users/mutations";
import { FormManuallyAddAccount } from "../../../../shared/components/FormManuallyAddAccount";
import { FormType } from "../../../../shared/components/FormManuallyAddAccount/FormType";
import {
  ManualAccountSubType,
  ManualAccountType
} from "../../../../shared/components/FormManuallyAddAccount/types";
import { StepLayoutWithOptions } from "../../../../shared/components/Layout/StepLayoutWithOptions";
import {
  avatarBankAccount,
  avatarBankCard,
  avatarPlaid
} from "../../../../shared/constants/avatars";
import { useMemberPlaidLink } from "../../../../utils/usePlaidLink";
import { selectorAccountDepositReady } from "../../../Transfer/components/FormTransfer/selectorAccountDepositReady";
import { FormAddCreditCard } from "../FormAddCreditCard";
import { FormSelectAccount } from "../FormSelectAccount";

export type PaymentMethodType = "ach" | "credit";
type PaymentMethodACHType = "plaid" | "manual";

type Props = {
  basePath: string;
  onBack?: () => void;
  onNext?: () => void;
  availableMethods?: PaymentMethodType[];
};

// Empty/undefined availableMethods means "no restriction" — show everything.
const filterOptionsByAvailableMethods = <O extends { value: string }>(
  options: O[],
  availableMethods: PaymentMethodType[] | undefined
): O[] => {
  if (!availableMethods) {
    return options;
  }
  const allowedValues = availableMethods as string[];
  return options.filter(option => allowedValues.includes(option.value));
};

export const FlowAddPaymentMethod: React.FC<Props> = ({
  basePath,
  onBack,
  onNext,
  availableMethods
}) => {
  const history = useHistory();
  const [createAccount] = useMutationCreateAccount();
  const [createdAccounts, setCreatedAccounts] = useState<IAccount[]>([]);

  const [manualAccountType, setManualAccountType] = useState<{
    type: ManualAccountType;
    subType: ManualAccountSubType;
  }>({
    type: "personal",
    subType: "checking"
  });

  const { openSnackbar } = useWSSnackbar();

  const plaidHandler = useMemberPlaidLink({
    onSuccess: async (publicToken: string) => {
      const createResponse = await createAccount({
        publicToken,
        status: AccountStatus.Pending
      });

      let accounts = createResponse
        ? Array.isArray(createResponse)
          ? createResponse
          : [createResponse]
        : [];

      const filteredAccounts = accounts.filter(selectorAccountDepositReady);

      if (filteredAccounts.length > 0) {
        setCreatedAccounts(accounts);

        history.push(basePath + "/accounts");

        return {
          success: true,
          data: accounts
        };
      } else {
        openSnackbar({
          type: "error",
          message: "Account can not be used as a payroll funding method"
        });

        return {
          success: false
        };
      }
    }
  });

  return (
    <Switch>
      <Route path={basePath + "/type"}>
        <StepLayoutWithOptions<PaymentMethodType>
          key="type"
          title="Add payment method"
          options={filterOptionsByAvailableMethods(
            [
              {
                header: {
                  label: {
                    text: "Bank account",
                    helperText: "Connect your bank account securely",
                    avatar: avatarBankAccount
                  }
                },
                value: "ach"
              },
              {
                header: {
                  label: {
                    text: "Credit card",
                    helperText: "Credit card",
                    avatar: avatarBankCard
                  }
                },
                value: "credit"
              }
            ],
            availableMethods
          )}
          onBack={onBack}
          onNext={value => {
            history.push(basePath + "/" + value);
          }}
        />
      </Route>

      <Route path={basePath + "/ach"}>
        <StepLayoutWithOptions<PaymentMethodACHType>
          key="ach"
          title="Add bank account"
          description="Add a bank account as your payment method"
          options={[
            {
              header: {
                label: {
                  text: "Link account with Plaid",
                  helperText:
                    "Instantly verify and connect your bank accounts securely",
                  avatar: avatarPlaid
                }
              },
              value: "plaid"
            },
            {
              header: {
                label: {
                  text: "Set up account manually",
                  helperText:
                    "Manually enter your routing and account numbers and verify via micro-deposits",
                  avatar: avatarBankAccount
                }
              },
              value: "manual"
            }
          ]}
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onNext={value => {
            if (value === "plaid") {
              plaidHandler.open();
            } else {
              history.push(basePath + "/manual-type");
            }
          }}
        />
      </Route>

      <Route path={basePath + "/manual-type"}>
        <FormType
          value={manualAccountType}
          onBack={() => {
            history.push(basePath + "/ach");
          }}
          onNext={newValue => {
            setManualAccountType(newValue);
            history.push(basePath + "/manual");
          }}
        />
      </Route>

      <Route path={basePath + "/manual"}>
        <FormManuallyAddAccount
          setDefaultPaymentMethod
          requestVerification
          onBack={() => {
            history.push(basePath + "/manual-type");
          }}
          onSuccess={onNext}
          withPanel
          type={manualAccountType.type}
          subType={manualAccountType.subType}
        />
      </Route>

      <Route path={basePath + "/credit"}>
        <FormAddCreditCard
          setPaymentMethod
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onNext={onNext}
        />
      </Route>

      <Route path={basePath + "/accounts"}>
        <FormSelectAccount
          title="Select bank account"
          description="Select a bank account as your default payroll funding method"
          setPaymentMethod
          accounts={createdAccounts}
          onBack={() => {
            history.push(basePath + "/ach");
          }}
          onNext={onNext}
        />
      </Route>

      <Redirect path={basePath} to={basePath + "/type"} />
    </Switch>
  );
};
